import { defineStore } from 'pinia'
import type { IUser } from '~/types'

import type { ICreateUserData } from '~/components/warehouseman/types'
import type { IUpdateUserData } from '~/components/auth/update-userdata-form/types'

interface IContacts {
  id: string
  code: string
  title: string
}

interface IChangePasswordData {
  currentPassword: string
  newPassword: string
  confirmNewPassword: string
}

export const useAuthStore = defineStore('auth', () => {
  const currentUser = ref<IUser | null>(null)
  const contacts = ref<IContacts[]>([])

  async function login(email: string, password: string): Promise<boolean> {
    try {
      const response = await useApi('api/auth/login', {
        method: 'POST',
        body: { login: email, password },
      })

      if (response?.data?.token) {
        localStorage.setItem('token', response.data.token)
        navigateTo('/')
        return true
      }

      if (response?.error) {
        throw new Error(response.error.message || 'Login failed')
      }

      throw new Error('Invalid server response')
    } catch (error) {
      console.error('Login error:', error)
      return false
    }
  }

  async function logout() {
    await useApi('api/auth/logout', {
      method: 'POST',
    })
    localStorage.removeItem('token')
    navigateTo('/login')
  }

  async function getContacts() {
    try {
      const response = await useApi('api/catalog/profile', {
        method: 'GET',
      })
      contacts.value = response?.data?.contactType || []
      return contacts.value
    } catch (error) {
      console.error('Failed to fetch contacts:', error)
      contacts.value = []
      return contacts.value
    }
  }

  async function changePassword(pass: IChangePasswordData) {
    try {
      const response = await useApi('api/auth/change-password', {
        method: 'POST',
        body: pass,
      })

      if (response.success) {
        return true
      }

      throw new Error(response.message || 'Password change failed')
    } catch (error) {
      console.error('Password change error:', error)
      return false
    }
  }

  async function getCurrentUser(): Promise<IUser | null> {
    try {
      const response = await useApi('api/user/profile', {
        method: 'GET',
      })
      currentUser.value = response.data
      return response.data
    } catch (error) {
      console.error('Failed to fetch current user:', error)
      return null
    }
  }

  async function updateUser(userData: IUpdateUserData) {
    try {
      if (!currentUser.value) {
        throw new Error('No authenticated user')
      }

      const updatedUser = await useApi('api/user/update', {
        method: 'POST',
        body: userData,
      })
      if (updatedUser.success) {
        await getCurrentUser()
        return true
      }

      throw new Error(updatedUser.message || 'User update failed')
    } catch (error) {
      console.error('Failed to update user:', error)
      return false
    }
  }

  async function createUser(userData: ICreateUserData): Promise<IUser | null> {
    try {
      const response = await useApi('api/admin/user/create-admin', {
        method: 'POST',
        body: userData,
      })

      return response.data as IUser
    } catch (error) {
      console.error('User creation failed:', error)

      if (error) {
        console.error('User creation failed:', error)
        navigateTo('/')
      }

      return null
    }
  }

  // нужно, но пока не используется

  /* async function impersonate(userId: string) {
    try {
      const response = await useApi(`/admin/impersonate/${userId}`, {
        method: "POST",
        headers: { "X-Impersonate": "true" },
      });

      if (!response?.token) throw new Error("No token received");

      if (import.meta.client) {
        localStorage.setItem("token", response.token);
        await this.getCurrentUser();
      }
    } catch (error) {
      console.error("Impersonation failed:", error);
      if (import.meta.client) {
        localStorage.removeItem("token");
      }
    }
  },
  async function leaveImpersonation() {
    try {
      const response = await useApi("/admin/impersonate/leave", {
        method: "POST",
        headers: { "X-Impersonate-Leave": "true" },
      });

      if (response) {
        if (import.meta.client) {
          localStorage.setItem("token", response.token);
          await this.getCurrentUser();
        }
      }
    } catch (error) {
      console.error("Leave impersonation failed:", error);
    }
  }, */
  async function resetPassword(email: string) {
    try {
      const response = await useApi('api/auth/reset-password', {
        method: 'POST',
        body: { email },
      })

      if (response.success) {
        console.log('Password reset email sent successfully')
        return true
      }
    } catch (error) {
      console.error('Failed to reset password:', error)
      return false
    }
  }

  return {
    login,
    logout,
    getCurrentUser,
    getContacts,
    changePassword,
    updateUser,
    createUser,
    resetPassword,
    contacts,
    currentUser,
  }
})
